// ────────────────────────────────────────────────────────────────────────────
// Bridges `Organisation.languageCode` (BE) → i18next.language (FE).
//
// Mounted once inside the authenticated tree (see `AppShell`). Watches the
// current organisation's payload and, whenever its `languageCode` differs
// from the active i18n language, calls `i18n.changeLanguage(...)`.
//
// Renders nothing — pure side-effect bridge, mirrors the `AuthTokenBridge`
// pattern used for the axios token wiring.
//
// Behaviour notes:
//   * The org payload is fetched lazily by other screens. The *boot*
//     language is whatever `resolveBootLanguage()` decided (cached pref,
//     browser hint, or English). Once the org lands, that value is the
//     authoritative override.
//   * Every successful org-driven change is also written to localStorage
//     via `persistLanguage()`, so the next boot starts on the right foot
//     instead of flashing English first.
//   * Codes the build doesn't ship translations for fall back to English
//     silently, so an operator who toggles their org to e.g. `de` before
//     we publish a German bundle still sees a usable UI rather than
//     missing-key placeholders.
//   * Comparison is case-insensitive — BE may emit `en-IE` / `en` / `EN`.
// ────────────────────────────────────────────────────────────────────────────
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganisation } from '@my/directory-api/hooks';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { DEFAULT_LANGUAGE, isSupportedLanguage, persistLanguage } from './';

export function SyncLanguageWithOrg() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { data: org } = useOrganisation(orgId);
  const { i18n } = useTranslation();

  useEffect(() => {
    const raw = org?.languageCode;
    if (!raw) return;

    // Strip a region tag if present (`en-IE` → `en`) before we test the
    // build's supported set. We can revisit fully-qualified locales when
    // we ship region-specific bundles.
    const code = raw.toLowerCase().split('-')[0] ?? '';
    const target = isSupportedLanguage(code) ? code : DEFAULT_LANGUAGE;

    // Always cache — even when target === current language. Cheap, and
    // covers the case where the cache somehow drifted from the live
    // value (e.g. dev cleared i18n state but not localStorage).
    persistLanguage(target);

    if (i18n.language !== target) {
      void i18n.changeLanguage(target);
    }
  }, [org?.languageCode, i18n]);

  return null;
}
